/**
 * AS3 (AVM2) semantic helpers used by the transpiled game code (tools/as3-to-ts).
 *
 * Pure TypeScript, no DOM. Nondeterministic sources (random numbers, wall clock, trace output)
 * are injectable through `configureRuntime` so headless runs and replays stay deterministic
 * (docs/architecture.md section 4.4).
 */

// ---------------------------------------------------------------- coercions
/** AS3 `int(x)` / assignment to an `int` slot: ToInt32(ToNumber(x)); NaN/undefined -> 0, truncation, 32-bit wrap. */
export function int(v?: any): number {
  return v | 0;
}
/** AS3 `uint(x)` / assignment to a `uint` slot: ToUint32(ToNumber(x)). */
export function uint(v?: any): number {
  return v >>> 0;
}
/** Assignment to a `String` slot: null/undefined -> null, anything else -> String(x). */
export function str(v: any): any {
  return v == null ? null : String(v);
}
/** AS3 `Object(x)` conversion: null/undefined -> new Object, otherwise x (no primitive wrappers in AS3). */
export function toObject(v?: any): any {
  return v == null ? {} : v;
}

export const INT_MAX_VALUE = 2147483647;
export const INT_MIN_VALUE = -2147483648;
export const UINT_MAX_VALUE = 4294967295;
export const UINT_MIN_VALUE = 0;

/** AS3 `Array` sort option flags. */
export const ArrayFlags = {
  CASEINSENSITIVE: 1,
  DESCENDING: 2,
  UNIQUESORT: 4,
  RETURNINDEXEDARRAY: 8,
  NUMERIC: 16,
} as const;

/** AS3-only error classes. */
export class ArgumentError extends Error {
  constructor(message = '', public errorID = 0) {
    super(message);
    this.name = 'ArgumentError';
  }
}
export class SecurityError extends Error {
  constructor(message = '', public errorID = 0) {
    super(message);
    this.name = 'SecurityError';
  }
}

// ---------------------------------------------------------------- is / as
/** Primitive AS3 type names are passed as strings ('int', 'uint', 'Number', 'String', 'Boolean', 'Class'). */
export type AS3TypeRef = string | Function;

/** AS3 `v is T`. */
export function is(v: any, T: AS3TypeRef): boolean {
  if (typeof T === 'string') {
    switch (T) {
      case 'int':
        return typeof v === 'number' && (v | 0) === v;
      case 'uint':
        return typeof v === 'number' && v >>> 0 === v;
      case 'Number':
        return typeof v === 'number';
      case 'String':
        return typeof v === 'string';
      case 'Boolean':
        return typeof v === 'boolean';
      case 'Class':
        return typeof v === 'function';
      default:
        throw new Error(`as3.is: unknown primitive type ${T}`);
    }
  }
  if (v === null || v === undefined) return false;
  if (T === Object) return true;
  if (T === Array) return Array.isArray(v);
  if (T === Function) return typeof v === 'function';
  if (T === Number) return typeof v === 'number';
  if (T === String) return typeof v === 'string';
  if (T === Boolean) return typeof v === 'boolean';
  return typeof v === 'object' || typeof v === 'function' ? v instanceof (T as any) : false;
}

/** AS3 `v as T`: v when `v is T`, otherwise null. */
export function as(v: any, T: AS3TypeRef): any {
  return is(v, T) ? v : null;
}

// ---------------------------------------------------------------- for-in / for-each
function isIndexKey(k: string): boolean {
  return /^(0|[1-9][0-9]*)$/.test(k) && Number(k) < 4294967295;
}

/**
 * Keys enumerated by AS3 `for (k in o)`. AVM2 yields array indices / integer-like names as numbers,
 * everything else as strings. Order: array indices ascending, then other own enumerable properties in
 * insertion order.
 * UNVERIFIED: Flash Player enumerates dynamic Object properties in hash-table order, which is not
 * reproducible; insertion order is used instead.
 */
export function keys(o: any): any[] {
  if (o == null || (typeof o !== 'object' && typeof o !== 'function')) return [];
  const out: any[] = [];
  for (const k of enumerableKeys(o)) out.push(isIndexKey(k) ? Number(k) : k);
  return out;
}

/** Values enumerated by AS3 `for each (v in o)` (same order as `keys`; array holes are skipped). */
export function values(o: any): any[] {
  if (o == null || (typeof o !== 'object' && typeof o !== 'function')) return [];
  return enumerableKeys(o).map((k) => o[k]);
}

/**
 * Own enumerable names AS3 would enumerate. For runtime class instances (display objects, BitmapData, ...)
 * the Flash runtime's internal `$`-prefixed slots are sealed traits in AVM2 and are skipped; only dynamic
 * properties (e.g. timeline instance names, values set by game code) remain.
 */
function enumerableKeys(o: any): string[] {
  const all = Object.keys(o);
  if (Array.isArray(o)) return all;
  const proto = Object.getPrototypeOf(o);
  if (proto === Object.prototype || proto === null) return all;
  return all.filter((k) => k.charCodeAt(0) !== 36);
}

// ---------------------------------------------------------------- sorting (avmplus ArraySort port)
function isObjectAtom(v: any): boolean {
  return v !== null && (typeof v === 'object' || typeof v === 'function');
}
function numCompare(x: number, y: number): number {
  const diff = x - y;
  if (diff === diff) return diff < 0 ? -1 : diff > 0 ? 1 : 0;
  if (!Number.isNaN(y)) return 1;
  if (!Number.isNaN(x)) return -1;
  return 0;
}
function strCompare(x: string, y: string): number {
  return x < y ? -1 : x > y ? 1 : 0;
}

/** Iterative quicksort over an index permutation, identical to avmplus ArraySort::qsort (unstable). */
function qsort(index: number[], compare: (a: number, b: number) => number, lo: number, hi: number): void {
  const swap = (a: number, b: number) => {
    const t = index[a];
    index[a] = index[b];
    index[b] = t;
  };
  const stk: [number, number][] = [];
  if (lo >= hi) return;
  for (;;) {
    const size = hi - lo + 1;
    if (size < 4) {
      if (size === 3) {
        if (compare(lo, lo + 1) > 0) {
          swap(lo, lo + 1);
          if (compare(lo + 1, lo + 2) > 0) {
            swap(lo + 1, lo + 2);
            if (compare(lo, lo + 1) > 0) swap(lo, lo + 1);
          }
        } else if (compare(lo + 1, lo + 2) > 0) {
          swap(lo + 1, lo + 2);
          if (compare(lo, lo + 1) > 0) swap(lo, lo + 1);
        }
      } else if (size === 2) {
        if (compare(lo, lo + 1) > 0) swap(lo, lo + 1);
      }
    } else {
      const pivot = lo + Math.floor(size / 2);
      swap(pivot, lo);
      let left = lo;
      let right = hi + 1;
      for (;;) {
        do left++;
        while (left <= hi && compare(left, lo) <= 0);
        do right--;
        while (right > lo && compare(right, lo) >= 0);
        if (right < left) break;
        swap(left, right);
      }
      swap(lo, right);
      if (right - 1 - lo >= hi - left) {
        if (lo + 1 < right) stk.push([lo, right - 1]);
        if (left < hi) {
          lo = left;
          continue;
        }
      } else {
        if (left < hi) stk.push([left, hi]);
        if (lo + 1 < right) {
          hi = right - 1;
          continue;
        }
      }
    }
    const top = stk.pop();
    if (!top) return;
    [lo, hi] = top;
  }
}

interface SortPlan {
  /** Values compared (element values, or field values for single-field sortOn). */
  atoms: any[];
  /** Values written back (defaults to atoms). */
  out?: any[];
  compare: (a: any, b: any) => number;
  /** true: move non-objects to the end (single-field sortOn); false: move undefined to the end. */
  objectsOnly: boolean;
  options: number;
}

function runSort(arr: any, plan: SortPlan): any {
  const len = arr.length >>> 0;
  if (len === 0 || len >= 0x10000000) return arr;
  const index: number[] = new Array(len);
  let j = len;
  let newlen = len;
  for (let i = len - 1; i >= 0; i--) {
    index[i] = i;
    const a = arr[i];
    const moveToEnd = plan.objectsOnly ? !isObjectAtom(a) : a === undefined;
    if (moveToEnd) {
      j--;
      const temp = index[i];
      index[i] = index[j];
      if (!(i in arr)) {
        newlen--;
        index[j] = index[newlen];
        index[newlen] = temp;
      } else {
        index[j] = temp;
      }
    }
  }
  const get = (i: number) => plan.atoms[index[i]];
  const cmp = (l: number, r: number) => plan.compare(get(l), get(r));
  qsort(index, cmp, 0, j - 1);
  if (plan.options & ArrayFlags.UNIQUESORT) {
    for (let i = 0; i < len - 1; i++) if (cmp(i, i + 1) === 0) return 0;
  }
  if (plan.options & ArrayFlags.RETURNINDEXEDARRAY) return index.slice();
  const src = plan.out ?? plan.atoms;
  for (let i = 0; i < newlen; i++) arr[i] = src[index[i]];
  for (let i = newlen; i < len; i++) delete arr[i];
  return arr;
}

function makeCompare(options: number, script: ((a: any, b: any) => any) | null): (a: any, b: any) => number {
  let base: (a: any, b: any) => number;
  if (script) {
    // ScriptCompareCompatible: the result is truncated with ToInteger before its sign is taken.
    base = (a, b) => {
      const r = Math.trunc(Number(script(a, b))) || 0;
      return r > 0 ? 1 : r < 0 ? -1 : 0;
    };
  } else if (options & ArrayFlags.NUMERIC) base = (a, b) => numCompare(Number(a), Number(b));
  else if (options & ArrayFlags.CASEINSENSITIVE) base = (a, b) => strCompare(String(a).toLowerCase(), String(b).toLowerCase());
  else base = (a, b) => strCompare(String(a), String(b));
  return options & ArrayFlags.DESCENDING ? (a, b) => base(b, a) : base;
}

/** AS3 `Array.sort(...args)` (compare function and/or option flags), avmplus semantics. */
export function sort(arr: any, ...args: any[]): any {
  let script: ((a: any, b: any) => any) | null = null;
  let options = 0;
  if (typeof args[0] === 'function') {
    script = args[0];
    options = args.length > 1 ? Number(args[1]) | 0 : 0;
  } else if (args.length > 0) options = Number(args[0]) | 0;
  const len = arr.length >>> 0;
  const atoms: any[] = new Array(len);
  for (let i = 0; i < len; i++) {
    atoms[i] = arr[i];
    if (!script && options & ArrayFlags.NUMERIC && typeof atoms[i] !== 'number' && atoms[i] !== undefined && Number.isNaN(Number(atoms[i]))) {
      throw new TypeError(`Error #1034: Type Coercion failed: cannot convert ${String(atoms[i])} to Number.`);
    }
  }
  return runSort(arr, { atoms, compare: makeCompare(options, script), objectsOnly: false, options });
}

/**
 * AS3 `Array.sortOn(fieldName, options)`: fieldName is a String or an Array of Strings,
 * options a flag set or an Array of flag sets (one per field). avmplus semantics, including the
 * unstable quicksort order of equal keys.
 */
export function sortOn(arr: any, names: any, options: any = 0): any {
  const fields: string[] = Array.isArray(names) ? names.map(String) : [String(names)];
  const opts: number[] = fields.map((_, i) => (Array.isArray(options) ? Number(options[i]) | 0 : Number(options) | 0));
  const overall = Array.isArray(options) ? Number(options[0]) | 0 : Number(options) | 0;
  const len = arr.length >>> 0;
  if (fields.length === 1) {
    const name = fields[0];
    const out: any[] = new Array(len);
    const atoms: any[] = new Array(len);
    for (let i = 0; i < len; i++) {
      out[i] = arr[i];
      if (isObjectAtom(arr[i])) atoms[i] = arr[i][name];
    }
    return runSort(arr, { atoms, out, compare: makeCompare(opts[0], null), objectsOnly: true, options: overall });
  }
  // Multi-field FieldCompare path.
  const atoms: any[] = new Array(len);
  for (let i = 0; i < len; i++) atoms[i] = arr[i];
  const compare = (j: any, k: any): number => {
    let opt = overall;
    let result = 0;
    const oj = isObjectAtom(j) ? j : null;
    const ok = isObjectAtom(k) ? k : null;
    if (!(oj && ok)) {
      result = ok ? 1 : oj ? -1 : 0;
      return opt & ArrayFlags.DESCENDING ? -result : result;
    }
    for (let f = 0; f < fields.length; f++) {
      const name = fields[f];
      opt = opts[f];
      const x = oj[name];
      const y = ok[name];
      const dx = x !== undefined;
      const dy = y !== undefined;
      if (!(dx && dy)) {
        if (dy) result = 1;
        else if (dx) result = -1;
        else {
          const hx = name in oj;
          const hy = name in ok;
          result = !hx && hy ? 1 : hx && !hy ? -1 : 0;
        }
      } else if (opt & ArrayFlags.NUMERIC) result = numCompare(Number(x), Number(y));
      else {
        let sx = String(x);
        let sy = String(y);
        if (opt & ArrayFlags.CASEINSENSITIVE) {
          sx = sx.toLowerCase();
          sy = sy.toLowerCase();
        }
        result = strCompare(sx, sy);
      }
      if (result !== 0) break;
    }
    return opt & ArrayFlags.DESCENDING ? -result : result;
  };
  return runSort(arr, { atoms, compare, objectsOnly: false, options: overall });
}

// ---------------------------------------------------------------- AMF3 deep copy
/**
 * Deep copy equivalent to `ByteArray.writeObject(v); position = 0; readObject()` for the data the game
 * clones (plain Objects, Arrays, Dates, primitives). Shared references and cycles are preserved like AMF3
 * object references. Functions are dropped. UNVERIFIED for instances of classes (AMF3 would serialize
 * public properties into an anonymous Object; here own enumerable properties are copied).
 */
export function amfClone(v: any, seen: Map<any, any> = new Map()): any {
  if (v === null || v === undefined) return v;
  if (typeof v === 'function') return undefined;
  if (typeof v !== 'object') return v;
  if (seen.has(v)) return seen.get(v);
  if (v instanceof Date) {
    const d = new Date(v.getTime());
    seen.set(v, d);
    return d;
  }
  if (Array.isArray(v)) {
    const out: any[] = [];
    seen.set(v, out);
    const src: any = v;
    for (const k of Object.keys(src)) {
      if (typeof src[k] !== 'function') (out as any)[k] = amfClone(src[k], seen);
    }
    return out;
  }
  const out: any = {};
  seen.set(v, out);
  for (const k of Object.keys(v)) {
    if (typeof v[k] === 'function') continue;
    out[k] = amfClone(v[k], seen);
  }
  return out;
}

// ---------------------------------------------------------------- bytes (MD5 support)
/** UTF-8 bytes of a string (ByteArray.writeUTFBytes). */
export function utf8Bytes(s: any): number[] {
  const str = String(s);
  const out: number[] = [];
  for (let i = 0; i < str.length; i++) {
    let c = str.charCodeAt(i);
    if (c >= 0xd800 && c <= 0xdbff && i + 1 < str.length) {
      const d = str.charCodeAt(i + 1);
      if (d >= 0xdc00 && d <= 0xdfff) {
        c = 0x10000 + ((c - 0xd800) << 10) + (d - 0xdc00);
        i++;
      }
    }
    if (c < 0x80) out.push(c);
    else if (c < 0x800) out.push(0xc0 | (c >> 6), 0x80 | (c & 63));
    else if (c < 0x10000) out.push(0xe0 | (c >> 12), 0x80 | ((c >> 6) & 63), 0x80 | (c & 63));
    else out.push(0xf0 | (c >> 18), 0x80 | ((c >> 12) & 63), 0x80 | ((c >> 6) & 63), 0x80 | (c & 63));
  }
  return out;
}

/** Bytes written by `ByteArray.writeInt` for each value (big-endian, the ByteArray default). */
export function int32BytesBE(ints: number[]): number[] {
  const out: number[] = [];
  for (const v of ints) out.push((v >>> 24) & 255, (v >>> 16) & 255, (v >>> 8) & 255, v & 255);
  return out;
}

// ---------------------------------------------------------------- method binding
/**
 * AS3 methods are bound closures: `addEventListener(Event.ENTER_FRAME, this._main)` and a later
 * `removeEventListener(..., this._main)` must see the same function. Called at the start of every
 * transpiled constructor with that class's prototype; defines non-enumerable own bound copies of its
 * methods. The most-derived implementation is bound (virtual dispatch also works while a base-class
 * constructor runs).
 */
export function bindMethods(obj: any, proto: any): void {
  const actual = Object.getPrototypeOf(obj);
  for (const name of Object.getOwnPropertyNames(proto)) {
    if (name === 'constructor') continue;
    const d = Object.getOwnPropertyDescriptor(proto, name);
    if (!d || typeof d.value !== 'function') continue;
    const impl = actual[name];
    if (typeof impl !== 'function') continue;
    Object.defineProperty(obj, name, { value: impl.bind(obj), writable: true, configurable: true, enumerable: false });
  }
}

// ---------------------------------------------------------------- localization
export { language, translate, setLanguage, missingTranslations, type Language } from './i18n';
import { translate as translateString } from './i18n';

/**
 * Translation of a source (Russian) string literal of the game code. The transpiler wraps every literal with
 * Cyrillic letters in this call (tools/as3-to-ts), so all user-visible text goes through the active catalogue.
 */
export function tr(text: string): string {
  return translateString(text);
}

// ---------------------------------------------------------------- injectable sources
export interface RuntimeSources {
  /** Uniform random in [0, 1): replaces Math.random() in game code (non-gameplay "true_random" uses). */
  random: () => number;
  /** Wall clock in epoch milliseconds: replaces `new Date()` in game code. */
  now: () => number;
  /** Sink for AS3 trace(); the release Flash Player discards trace output. */
  trace: (...args: any[]) => void;
}

/** Deterministic default RNG (mulberry32) until the platform injects its own source. */
function mulberry32(seed: number): () => number {
  let a = seed >>> 0;
  return () => {
    a = (a + 0x6d2b79f5) | 0;
    let t = Math.imul(a ^ (a >>> 15), 1 | a);
    t = (t + Math.imul(t ^ (t >>> 7), 61 | t)) ^ t;
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
  };
}

/**
 * Default clock: fixed at 2018-09-17T21:38:36Z, the `time=1537220316` value embedded in the offline build's
 * assault data. Hosts must inject a real clock (browser/server) via configureRuntime({ now }).
 */
export const DEFAULT_EPOCH_MS = 1537220316000;

const sources: RuntimeSources = {
  random: mulberry32(1),
  now: () => DEFAULT_EPOCH_MS,
  trace: () => {},
};

/** Injects random/clock/trace sources (called by the platform bootstrap and by tests). */
export function configureRuntime(s: Partial<RuntimeSources>): void {
  if (s.random) sources.random = s.random;
  if (s.now) sources.now = s.now;
  if (s.trace) {
    sources.trace = s.trace;
    traceActive = true;
  }
}

/** Resets the sources to the deterministic defaults (tests). */
export function resetRuntime(seed = 1): void {
  sources.random = mulberry32(seed);
  sources.now = () => DEFAULT_EPOCH_MS;
  sources.trace = () => {};
  traceActive = false;
}

let traceActive = false;
/** True when a host injected a trace sink (debug builds / tests); release Flash Player discarded trace(). */
export function traceEnabled(): boolean {
  return traceActive;
}

export function random(): number {
  return sources.random();
}
export function now(): number {
  return sources.now();
}
/** `new Date()` with the injected clock. */
export function newDate(): Date {
  return new Date(sources.now());
}
/** AS3 `trace(...)`: arguments joined with a space, like Flash. */
export function trace(...args: any[]): void {
  sources.trace(args.map((a) => String(a)).join(' '));
}

// ---------------------------------------------------------------- JSON game state (backend profile)
/**
 * Converts AS3 runtime data (plain objects, possibly sparse arrays, NaN/Infinity, `ProtectedInt`-like
 * wrappers) into plain JSON that round-trips exactly through `fromJsonState`:
 * wrapper instances -> {"$pi": value}; sparse arrays -> {"$sparse": length, "e": [[index, value], ...]};
 * non-finite numbers -> {"$n": "NaN" | "Infinity" | "-Infinity"}; undefined -> {"$u": 1}.
 */
export function toJsonState(v: any, wrapper?: Function): any {
  if (v === undefined) return { $u: 1 };
  if (v === null || typeof v === 'string' || typeof v === 'boolean') return v;
  if (typeof v === 'number') return Number.isFinite(v) ? v : { $n: String(v) };
  if (wrapper && v instanceof (wrapper as any)) return { $pi: v.value };
  if (Array.isArray(v)) {
    const keys = Object.keys(v);
    const dense = keys.length === v.length && keys.every((k, i) => k === String(i));
    if (dense) return v.map((x) => toJsonState(x, wrapper));
    return { $sparse: v.length, e: keys.map((k) => [/^\d+$/.test(k) ? Number(k) : k, toJsonState((v as any)[k], wrapper)]) };
  }
  if (typeof v === 'object') {
    const out: Record<string, any> = {};
    for (const k of Object.keys(v)) out[k] = toJsonState(v[k], wrapper);
    return out;
  }
  return null;
}

/** Inverse of `toJsonState`; `makeWrapper` rebuilds {"$pi": n} entries (e.g. `(n) => new ProtectedInt(n)`). */
export function fromJsonState(v: any, makeWrapper?: (value: number) => any): any {
  if (v === null || typeof v !== 'object') return v;
  if (Array.isArray(v)) return v.map((x) => fromJsonState(x, makeWrapper));
  if ('$u' in v && Object.keys(v).length === 1) return undefined;
  if ('$n' in v && Object.keys(v).length === 1) return Number(v.$n);
  if ('$pi' in v && Object.keys(v).length === 1) return makeWrapper ? makeWrapper(Number(v.$pi)) : Number(v.$pi);
  if ('$sparse' in v && 'e' in v) {
    const arr: any[] = new Array(Number(v.$sparse));
    for (const [k, x] of v.e) (arr as any)[k] = fromJsonState(x, makeWrapper);
    return arr;
  }
  const out: Record<string, any> = {};
  for (const k of Object.keys(v)) out[k] = fromJsonState(v[k], makeWrapper);
  return out;
}
